<script setup lang="ts">
import {
  languagePickerKey,
  locationAutocompleteKey,
  type LanguagePickerApi,
  type LocationAutocompleteApi,
} from '@pkg/site-chrome';
import { useSanityData } from '@pkg/sanity-utils/composables/useSanityData';
import type { Origin } from '@pkg/types/surpriseBag';
import DraftModeBar from '@pkg/site-chrome/components/DraftModeBar.vue';
import { useSiteManifestQuery } from '~/app/composables/useSiteManifestQuery';
import { useSanityDefaultSeoMeta } from '~/app/composables/useSanityDefaultSeoMeta';
import { useLayoutHideHeader } from '~/app/composables/useLayoutHideHeader';
import { useLocationAutocomplete } from '~/app/composables/useLocationAutocomplete';
import { useSearchParams } from '~/app/composables/useSearchParams';
import type { PageContent } from '~/app/features/cms/types/PageContent';
import { pageBySlug } from '~/apis/sanity/queries/pageBySlug';

const toLocalePath = useLocalePath();
const { buildPathWithSearchParams } = useSearchParams();
const locationAutocomplete = useLocationAutocomplete();

/**
 * Build + provide before any `await`. Also pass as a prop — inject alone is unreliable
 * across the layout async boundary / package entry split.
 */
const locationAutocompleteApi: LocationAutocompleteApi = {
  suggestions: locationAutocomplete.suggestions,
  loadingSuggestions: locationAutocomplete.loadingSuggestions,
  fetchSuggestions: locationAutocomplete.fetchSuggestions,
  initialSearchValue: locationAutocomplete.initialSearchValue,
  handleLocationSelected: locationAutocomplete.handleLocationSelected,
  buildFindUrl: (option: Origin) =>
    toLocalePath(buildPathWithSearchParams('/find', option, { searchContext: 'header' })),
};
provide(locationAutocompleteKey, locationAutocompleteApi);

const languagePickerApi: LanguagePickerApi = {
  resolveLocaleSlug: async(locale, slugParts) => {
    const sanityData = await useSanityData<{ page?: PageContent | null }>(
      pageBySlug(slugParts),
      { locale, ...slugParts, slug: slugParts?.slug || '' },
    );
    return sanityData?.data.value?.page?.slug ?? null;
  },
};
provide(languagePickerKey, languagePickerApi);

const { data } = await useSiteManifestQuery();
const { hideHeader } = useLayoutHideHeader();

if (data?.value?.defaultSeo) {
  useSanityDefaultSeoMeta(data.value.defaultSeo);
}
</script>

<template>
  <div class="sticky-footer">
    <div class="c-layout">
      <NuxtErrorBoundary>
        <PartialHeader
          v-if="!hideHeader"
          :menu-content="data?.regionTheme?.mainNavigation ?? null"
          :enable-location-search="true"
          :location-autocomplete="locationAutocompleteApi"
        />
      </NuxtErrorBoundary>

      <NuxtErrorBoundary>
        <main id="main">
          <slot />
        </main>
      </NuxtErrorBoundary>
    </div>

    <NuxtErrorBoundary>
      <DraftModeBar />
      <PartialFooter
        :social-links="data?.regionTheme?.mainFooter?.socialLinks ?? []"
        :page-links="data?.regionTheme?.mainFooter?.pageLinks ?? []"
        :legal-links="data?.regionTheme?.mainFooter?.legalLinks ?? []"
        :footnote="data?.regionTheme?.mainFooter?.footnote ?? ''"
      />
    </NuxtErrorBoundary>
  </div>
</template>

<style scoped>
/**
 * [1] Without this, the parallax part of the footer leaks through, as the "page" doesn't have a background color.
 * [2] Without this, the footer sits on top of the page, and the parallax effect doesn't work. Page must be above it.
 */

.c-layout {
  background-color: theme('colors.beige.30'); /* [1] */
  position: relative; /* [2] */
  z-index: theme('zIndex.page'); /* [2] */
}
</style>
